import { PostItem, Text, VirtuosoList } from 'components';
import React, { useCallback, useEffect } from 'react';
import { TPostDetail, TPostResponse } from 'types';
import { PAGE_SIZE, TEXT_STRING } from '../../constants';
import LoadingPost from 'components/PostItem/LoadingPost';
import { useInfiniteQuery } from '@tanstack/react-query';
import { StateSnapshot, VirtuosoHandle } from 'react-virtuoso';
import { useFollow } from 'hooks';

type Props = {
  loading?: boolean;
  onClickAvatar?: () => void;
  queryKey: string[];
  getPostList(currentPage: number, pageSize: number): Promise<TPostResponse>;
  enabled?: boolean;
  virtuosoRef?: React.RefObject<VirtuosoHandle>;
  virtuosoState?: StateSnapshot | null;
  customScrollParent?: HTMLElement | null;
  onReady?: () => void;
  onMaleAvatarClick?: (user: TPostDetail['user']) => void;
};

function PostView({
  loading = false,
  onClickAvatar,
  queryKey,
  getPostList,
  enabled = false,
  virtuosoRef,
  virtuosoState,
  customScrollParent,
  onReady,
  onMaleAvatarClick,
}: Props) {
  // Hooks
  const { handleFollow } = useFollow();

  // Query
  const {
    data: listView,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => getPostList(pageParam, PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, currentPage) => {
      return lastPage?.totalPage > currentPage ? currentPage + 1 : undefined;
    },
    select: useCallback((data: { pages: TPostResponse[] }) => {
      return data.pages.flatMap((page) => page.data);
    }, []),
    enabled,
    refetchOnMount: false,
  });

  // Memo, callback
  const isInitialLoading = loading || isLoading || (isFetching && !listView);

  const onFollowFemale = useCallback(
    async (femaleId: number) => {
      await handleFollow(femaleId, false);
    },
    [handleFollow],
  );

  const renderPost = useCallback(
    (index: number, item: TPostDetail) => {
      return (
        <PostViewItem
          item={item}
          onClickAvatar={onClickAvatar}
          onFollowFemale={onFollowFemale}
          onMaleAvatarClick={onMaleAvatarClick}
        />
      );
    },
    [onClickAvatar, onFollowFemale, onMaleAvatarClick],
  );

  const renderLoading = useCallback(() => {
    return <LoadingPost index={listView?.length || 0} />;
  }, [listView?.length]);

  useEffect(() => {
    if (!isInitialLoading) {
      onReady?.();
    }
  }, [isInitialLoading, onReady]);

  return (
    <>
      {isLoading && (!listView || (listView as TPostDetail[]).length === 0) ? (
        <div className="flex flex-col">
          {Array.from({ length: 3 }).map((_, i) => (
            <LoadingPost key={i} index={i} />
          ))}
        </div>
      ) : !listView?.length ? (
        <>
          <Text center bold className={`py-20`}>
            {TEXT_STRING.COMMON.NO_DATA}
          </Text>
        </>
      ) : (
        <div className="flex-1">
          <VirtuosoList<TPostDetail>
            virtuosoRef={virtuosoRef}
            data={listView || []}
            hasNext={hasNextPage}
            isFetchingNext={isLoading}
            onReachEnd={fetchNextPage}
            renderItem={renderPost}
            renderLoading={renderLoading}
            className="flex-1"
            restoreStateFrom={virtuosoState ?? undefined}
            customScrollParent={customScrollParent ?? undefined}
          />
        </div>
      )}
    </>
  );
}

type PostViewItemProps = {
  item: TPostDetail;
  onClickAvatar?: () => void;
  onFollowFemale?: (femaleId: number) => void;
  onMaleAvatarClick?: (user: TPostDetail['user']) => void;
};

const PostViewItem = React.memo(
  ({
    item,
    onClickAvatar,
    onFollowFemale,
    onMaleAvatarClick,
  }: PostViewItemProps) => {
    const hasMedia = !!item.media?.length;
    return (
      <div
        style={{
          height: hasMedia ? undefined : 150,
          overflow: 'hidden',
        }}
      >
        <PostItem
          className="p-4"
          data={item}
          onClickAvatar={onClickAvatar}
          onFollowFemale={onFollowFemale}
          showThumbnail={false}
          onMaleAvatarClick={onMaleAvatarClick}
        />
        <hr className="solid my-8" />
      </div>
    );
  },
);

export default React.memo(PostView);
